// Debounced save scheduling with an explicit flush, so callers can guarantee
// a pending save is sent before the surface that scheduled it goes away
// (mode unmount, tab close). Framework-free so the policy is unit-testable.

export interface DebouncedSaver<T> {
  /** Record the latest value and (re)start the debounce timer. */
  schedule(value: T): void;
  /**
   * Send a pending save immediately. No-op when nothing is pending.
   * keepalive marks the save as fired during page teardown, where the
   * transport must outlive the document.
   */
  flush(keepalive?: boolean): void;
  /** True while a save is scheduled but not yet sent. */
  pending(): boolean;
}

export function createDebouncedSaver<T>(
  save: (value: T, opts: { keepalive: boolean }) => void,
  delayMs: number,
): DebouncedSaver<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latest: T;

  return {
    schedule(value: T) {
      latest = value;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        save(latest, { keepalive: false });
      }, delayMs);
    },
    flush(keepalive = false) {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      save(latest, { keepalive });
    },
    pending() {
      return timer !== null;
    },
  };
}
